import { Alert, Button, Col, Modal, Row, Tabs } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";

import { UserViews } from "../../../types/user_views";
import {
  getDiscordActiveMembers,
  getDiscordAtRiskMembers,
} from "../../lib/discordQueries";
import {
  AllMembers,
  AtRiskMember,
  DiscordMemberRpc,
  MemberTag,
} from "../../lib/discordTypes";
import { getSupaClient } from "../../lib/supabase";
import { useEndTime, useStartTime } from "../DateContext/DateContext";
import NewSearch from "../NewSearch/NewSearch.jsx";
import { useTagsContext } from "../TagsContext/TagsContext";
import CreateNewView from "./CreateNewView";
import CustomFilterTab from "./CustomFilterTab";
import DiscordMembersTable from "./DiscordMembersTable";
import MemberDetail from "./MemberDetail";
import useAllMembers from "./useAllMembers";

const { TabPane } = Tabs;

/**
 * Load at risk members for the given guild
 * @param guildId discord_guilds discord_id value
 * @returns
 *  data: at risk members data,
 *  isLoading: wether hook is still loading,
 *  updateData: state modifying function to update data
 *  filteredAtRiskMembersRoles: filtered at-risk members by role
 *  setFilteredAtRiskMembersRoles: state modifying function to update filtered members
 *  resetAll: function to reset the filtered members
 *  loadAtRisk: callback to load at ris members from Supabase
 */
function useAtRiskMembers(guildId: string) {
  const [loading, setLoading] = useState<boolean>(true);
  const [atRiskMembers, setAtRiskMembers] = useState<AtRiskMember[] | null>([]);

  const [filteredAtRiskMembersRoles, setFilteredAtRiskMembersRoles] = useState<
    AtRiskMember[] | null
  >([]);

  const loadAtRisk = async (): Promise<void> => {
    setLoading(true);
    try {
      const data: AtRiskMember[] | null = await getDiscordAtRiskMembers(
        guildId
      );
      if (data) {
        setAtRiskMembers(data);
        setFilteredAtRiskMembersRoles(data);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setFilteredAtRiskMembersRoles(atRiskMembers);
  };

  // update if guild id changes
  useEffect(() => {
    loadAtRisk();
  }, [guildId]);

  // if empty
  if (!guildId) {
    const data: AtRiskMember[] = [];
    return {
      data,
      isLoading: false,
      updateData: setAtRiskMembers,
      setFilteredAtRiskMembersRoles,
      filteredAtRiskMembersRoles,
      resetAll,
      loadAtRisk,
    };
  }

  return {
    data: atRiskMembers,
    isLoading: loading,
    updateData: setAtRiskMembers,
    setFilteredAtRiskMembersRoles,
    filteredAtRiskMembersRoles,
    resetAll,
    loadAtRisk,
  };
}

/**
 * Load active members for the given guild and time period.
 * @param startTime start of time period, formatted as "MM/DD/YY"
 * @param endTime end of time period, formatted as "MM/DD/YY"
 * @param guildId discord_guilds discord_id value
 * @returns
 *  data: active members data,
 *  isLoading: whether hook is still loading,
 *  updateData: state modifying function to update data
 *  filteredActiveMembers: filtered active members by role
 *  setFilteredActiveMembers: state modifying function to update filtered members
 *  resetAll: function to reset filtered members
 *  loadActiveMembers: callback that reloads the active members from Supabase
 */
function useActiveMembers(startTime: string, endTime: string, guildId: string) {
  const [loading, setLoading] = useState<boolean>(true);
  const [activeMembers, setActiveMembers] = useState<DiscordMemberRpc[] | null>(
    []
  );
  const [filteredActiveMembers, setFilteredActiveMembers] = useState<
    DiscordMemberRpc[] | null
  >([]);

  const loadActiveMembers = async (): Promise<void> => {
    setLoading(true);
    try {
      const data: DiscordMemberRpc[] | null = await getDiscordActiveMembers(
        startTime,
        endTime,
        guildId
      );
      if (data) {
        setActiveMembers(data);
        setFilteredActiveMembers(data);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setFilteredActiveMembers(activeMembers);
  };

  // update if start time or end time changes
  useEffect(() => {
    loadActiveMembers();
  }, [guildId, startTime, endTime]);
  if (!guildId) {
    return {
      data: [],
      isLoading: false,
      updateData: setActiveMembers,
      setFilteredActiveMembers,
      filteredActiveMembers,
      resetAll,
      loadActiveMembers,
    };
  }

  return {
    data: activeMembers,
    isLoading: loading,
    updateData: setActiveMembers,
    setFilteredActiveMembers,
    filteredActiveMembers,
    resetAll,
    loadActiveMembers,
  };
}

/**
 * Load custom views for the given guild.
 * @param guildId discord_guilds discord_id value
 * @returns
 *  data: custom views data,
 *  isLoading: whether hook is still loading,
 *  refreshData: function to pull views again on change
 */
function useCustomViews(guildId: string) {
  const [loading, setLoading] = useState<boolean>(true);
  const [customViews, setCustomViews] = useState<UserViews[] | null>([]);
  const supabase = getSupaClient();

  const loadCustomViews = async (): Promise<void> => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from<UserViews>("user_views")
        .select("id, name")
        .eq("guild", guildId)
        .order("id", { ascending: true });
      if (data) {
        setCustomViews(data);
      }
      if (error) {
        console.log("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomViews();
  }, [guildId]);
  if (!guildId) {
    return {
      data: [],
      isLoading: false,
      refreshData: loadCustomViews,
    };
  }

  return {
    data: customViews,
    isLoading: loading,
    refreshData: loadCustomViews,
  };
}

const DiscordMembersView = () => {
  const guildId: string = localStorage.getItem("guild-id") || "";
  const atRiskMembers = useAtRiskMembers(guildId);
  const atRiskMembersdata: AtRiskMember[] | null = atRiskMembers?.data;
  const atRiskIsLoading: boolean = atRiskMembers?.isLoading;
  const { startTime, setStartTime } = useStartTime();
  const { endTime, setEndTime } = useEndTime();

  const activeMembers = useActiveMembers(startTime, endTime, guildId);
  const activeMembersData: DiscordMemberRpc[] | null = activeMembers?.data;
  const activeMembersLoading: boolean = activeMembers?.isLoading;
  const allMembers = useAllMembers(guildId);
  const customViews = useCustomViews(guildId);

  const [isUserModalVisibile, setIsUserModalVisibile] = useState(false);
  const [selectedMember, setSelectedMember] = useState<AllMembers | null>();

  const guildTags = useTagsContext();

  /**
   * Update a member's tag data in the source data for a member table, if the given member is
   * present in the table data.
   * @param membersData source data for a member table (state variable returned by custom hooks)
   * @param updateFn setState function for a member table (as returned by custom hooks)
   * @param memberId member id to update
   * @param memberTag new tag data to update member row with
   */
  async function addMemberTag<Type extends AllMembers>(
    membersData: Type[] | null,
    updateFn: React.Dispatch<React.SetStateAction<Type[] | null>>,
    memberId: string,
    memberTag: MemberTag
  ) {
    const memberIdx = membersData?.findIndex((m: any) => m.id === memberId);
    if (memberIdx !== undefined && memberIdx !== -1 && membersData) {
      const member = membersData[memberIdx];
      const newTag: MemberTag = {
        id: memberTag.id,
        name: memberTag.name,
        color: memberTag.color,
      };
      let newTags = member.tags;
      if (newTags) newTags.push(newTag);
      else newTags = [newTag];
      member.tags = newTags;
      const newMembersData = [...membersData];
      newMembersData[memberIdx] = member;
      updateFn(newMembersData);
    }
  }

  /**
   * Remove a tag from a member's tag data in the source data for a member table, if the given
   * member is present in the table data.
   * @param membersData source data for a member table (state variable returned by custom hooks)
   * @param updateFn setState function for a member table (as returned by custom hooks)
   * @param memberId member id to update
   * @param memberTag tag to remove from the member row
   */
  async function removeMemberTag<Type extends AllMembers>(
    membersData: Type[] | null,
    updateFn: React.Dispatch<React.SetStateAction<Type[] | null>>,
    memberId: string,
    memberTag: MemberTag
  ) {
    const memberIdx = membersData?.findIndex((m: any) => m.id === memberId);
    if (memberIdx !== undefined && memberIdx !== -1 && membersData) {
      const member = membersData[memberIdx];
      let newTags = member.tags;
      if (newTags) {
        newTags = newTags.filter((tag) => tag.id !== memberTag.id);
        member.tags = newTags;
        const newMembersData = [...membersData];
        newMembersData[memberIdx] = member;
        updateFn(newMembersData);
      }
    }
  }

  /**
   * Propagate a new member tag across all source data tables on the Discord members tab
   * @param memberId discord_members id
   * @param memberTag new tag that should be propagated to all data tables
   */
  const addMemberTagInTables = async (
    memberId: string,
    memberTag: MemberTag
  ) => {
    addMemberTag(
      activeMembers.data,
      activeMembers.updateData,
      memberId,
      memberTag
    );
    addMemberTag(
      atRiskMembers.data,
      atRiskMembers.updateData,
      memberId,
      memberTag
    );
    addMemberTag(
      allMembers.data,
      allMembers.setFilteredMembersRoles,
      memberId,
      memberTag
    );
  };

  /**
   * Propagate a removed member tag across all source data tables on the Discord members tab
   * @param memberId discord_members id
   * @param memberTag removed tag that should be propagated to all data tables
   */
  const removeMemberTagInTables = async (
    memberId: string,
    memberTag: MemberTag
  ) => {
    removeMemberTag(
      activeMembers.data,
      activeMembers.updateData,
      memberId,
      memberTag
    );
    removeMemberTag(
      atRiskMembers.data,
      atRiskMembers.updateData,
      memberId,
      memberTag
    );
    removeMemberTag(
      allMembers.data,
      allMembers.setFilteredMembersRoles,
      memberId,
      memberTag
    );
  };

  /**
   * Reload data in the all members, at-risk member and active members tabs. This is typically used
   * to reload data upon successfully bulk tagging members. It's going to be slow but is a temporary
   * measure to make sure bulk tagged users are consistent across tabs. It's not needed for custom
   * tabs as they are reloaded every time they are clicked into.
   */
  const reloadStandardViews = async () => {
    allMembers.loadAllMembers();
    atRiskMembers.loadAtRisk();
    activeMembers.loadActiveMembers();
  };

  // TODO: remove ANY here
  const customTabs: any = [];

  // eslint-disable-next-line no-restricted-syntax
  if (customViews.data) {
    for (let i = 0; i < customViews.data.length; i += 1) {
      const customView = customViews.data[i];
      customTabs.push(
        <TabPane
          tab={customView.name}
          key={`custom-${customView.id}`}
          style={{ paddingTop: 20 }}
        >
          <CustomFilterTab
            viewId={customView.id}
            startTime={startTime}
            endTime={endTime}
            loadViews={customViews.refreshData}
            onGuildTagUpdate={guildTags.updateData}
            onMemberTagAdded={addMemberTagInTables}
            onMemberTagRemoved={removeMemberTagInTables}
            onBulkTag={reloadStandardViews}
            setIsBulkEditModalVisible={guildTags.setIsBulkEditModalVisible}
            isBulkEditModalVisible={guildTags.isBulkEditModalVisible}
          />
        </TabPane>
      );
    }
  }
  /**
   * Handles the display of the member details modal
   *
   * @param record -  an object with the member's details
   */
  const showUserModal = async (record: any) => {
    setIsUserModalVisibile(true);
    setSelectedMember(record);
    window.analytics.track(`Member - member detail viewwed`);
  };

  /**
   * Gets the searched member profile's details and handles their display in the modal
   *
   * @param id - The Discord member discord_id
   */
  const openSearchedMemberProfile = (id: string) => {
    const record = allMembers.data?.find((item) => item.discord_id === id);
    showUserModal({ ...record });
  };

  /**
   * Closes the user detail modal
   */
  const handleUserCancel = () => {
    setIsUserModalVisibile(false);
  };

  return (
    <div>
      <Row style={{ paddingBottom: 40 }}>
        <Col span={8} />
        <Col span={8}>
          <NewSearch
            placeholder="Search for members"
            items={allMembers.data}
            platform="discord"
            handleSelection={openSearchedMemberProfile}
          />
        </Col>
        <Col span={8} style={{ textAlign: "end" }} />
      </Row>{" "}
      <Tabs defaultActiveKey="1" destroyInactiveTabPane>
        <TabPane tab="All members" key="1" style={{ paddingTop: 20 }}>
          <DiscordMembersTable
            description={`Your community currently has ${
              allMembers.totalMembersCounter || 0
            } members`}
            members={allMembers.data}
            membersFiltered={allMembers.filteredMembersRoles}
            isLoading={allMembers.isLoading}
            guildTags={guildTags.teamTags ? guildTags.teamTags : []}
            startTime={startTime}
            endTime={endTime}
            memberReset={allMembers.resetAll}
            memberUpdate={allMembers.setFilteredMembersRoles}
            onGuildTagUpdate={guildTags.updateData}
            onMemberTagAdded={addMemberTagInTables}
            onMemberTagRemoved={removeMemberTagInTables}
            showDatePicker={false}
            otherColumns={
              [
                {
                  key: "joined_at",
                  title: "Date joined",
                  dataIndex: "joined_at",
                  render: (text) => <p>{dayjs(text).format("MM/DD/YYYY")}</p>,
                  width: "15%",
                  sorter: (a: any, b: any) => {
                    if (a.joined_at > b.joined_at) return 1;
                    if (b.joined_at > a.joined_at) return -1;
                    return 0;
                  },
                },
              ] as ColumnsType<AllMembers | AtRiskMember | DiscordMemberRpc>
            }
            setIsBulkEditModalVisible={guildTags.setIsBulkEditModalVisible}
            isBulkEditModalVisible={guildTags.isBulkEditModalVisible}
            loadPageData={allMembers.loadAllMembers}
            onBulkTag={reloadStandardViews}
          />
        </TabPane>
        <TabPane tab="At risk members" key="2" style={{ paddingTop: 20 }}>
          <DiscordMembersTable
            description="Members that were active in the past, but not active recently"
            members={atRiskMembersdata}
            membersFiltered={atRiskMembers.filteredAtRiskMembersRoles}
            atRisk
            isLoading={atRiskIsLoading}
            startTime={startTime}
            endTime={endTime}
            guildTags={guildTags.teamTags ? guildTags.teamTags : []}
            memberUpdate={atRiskMembers.setFilteredAtRiskMembersRoles}
            memberReset={atRiskMembers.resetAll}
            onGuildTagUpdate={guildTags.updateData}
            onMemberTagAdded={addMemberTagInTables}
            onMemberTagRemoved={removeMemberTagInTables}
            showDatePicker={false}
            setIsBulkEditModalVisible={guildTags.setIsBulkEditModalVisible}
            isBulkEditModalVisible={guildTags.isBulkEditModalVisible}
            loadPageData={atRiskMembers.loadAtRisk}
            onBulkTag={reloadStandardViews}
          />
        </TabPane>
        <TabPane tab="Active members" key="3" style={{ paddingTop: 20 }}>
          <div className="flex flex-grow">
            <div>
              <Alert
                style={{
                  marginBottom: 20,
                  marginRight: 40,
                  marginLeft: 40,
                  background: "#e6f7ff",
                  borderColor: "#93d6ff",
                }}
                type="info"
                message="Members that have posted at least one or more message or reaction in the last 30 days (or selected time period)"
              />
            </div>
          </div>

          <DiscordMembersTable
            members={activeMembersData}
            membersFiltered={activeMembers.filteredActiveMembers}
            isLoading={activeMembersLoading}
            guildTags={guildTags.teamTags ? guildTags.teamTags : []}
            showActivityColumns
            startTime={startTime}
            endTime={endTime}
            memberReset={activeMembers.resetAll}
            memberUpdate={activeMembers.updateData}
            onGuildTagUpdate={guildTags.updateData}
            onMemberTagAdded={addMemberTagInTables}
            onMemberTagRemoved={removeMemberTagInTables}
            setIsBulkEditModalVisible={guildTags.setIsBulkEditModalVisible}
            isBulkEditModalVisible={guildTags.isBulkEditModalVisible}
            loadPageData={activeMembers.loadActiveMembers}
            onBulkTag={reloadStandardViews}
          />
        </TabPane>
        {customTabs}
        <TabPane tab="+ Custom tab" key="4" style={{ paddingTop: 20 }}>
          <CreateNewView onChange={customViews.refreshData} />
        </TabPane>
      </Tabs>
      <Modal
        width={1200}
        title={
          selectedMember
            ? `${selectedMember.username}'s activity`
            : "Member activity"
        }
        style={{ top: 40, right: 10, borderRadius: 20 }}
        visible={isUserModalVisibile}
        onCancel={handleUserCancel}
        footer={[
          <Button key="submit" onClick={handleUserCancel}>
            Ok
          </Button>,
        ]}
      >
        <MemberDetail
          discordUser={selectedMember}
          onMemberTagAdded={guildTags.updateData}
          platform="discord"
        />
      </Modal>
    </div>
  );
};

export default DiscordMembersView;
